import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type IntegrationSummary = {
  id: number;
  name: string;
  description: string | null;
  type: string;
  created_at: string;
  updated_at: string;
};

export type IntegrationDnsRecord = {
  type: string;
  name: string;
  value: string;
};

export type EmailIntegrationDetails = {
  domain: string;
  from_name: string;
  from_email: string;
  mail_from_subdomain: string;
  dns_records: IntegrationDnsRecord[];
  verification_status: string;
  dkim_status: string;
  mail_from_status: string;
  last_checked_at: string | null;
};

export type IntegrationDetail = IntegrationSummary & {
  email: EmailIntegrationDetails | null;
};

export type IntegrationsState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  connections: IntegrationSummary[];
  detail: {
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
    data: IntegrationDetail | null;
    /** Integration id of the in-flight request, so a superseded response is ignored. */
    requestedId: number | null;
  };
};

export const integrationsInitialState: IntegrationsState = {
  status: STATUS.IDLE,
  error: null,
  connections: [],
  detail: {
    status: STATUS.IDLE,
    error: null,
    data: null,
    requestedId: null,
  },
};

function isIntegrationSummary(value: unknown): value is IntegrationSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.name === "string" &&
    (row.description === null || typeof row.description === "string") &&
    typeof row.type === "string" &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string"
  );
}

function isIntegrationDnsRecord(value: unknown): value is IntegrationDnsRecord {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.type === "string" && typeof row.name === "string" && typeof row.value === "string"
  );
}

function isEmailIntegrationDetails(value: unknown): value is EmailIntegrationDetails {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.domain === "string" &&
    typeof row.from_name === "string" &&
    typeof row.from_email === "string" &&
    typeof row.mail_from_subdomain === "string" &&
    Array.isArray(row.dns_records) &&
    row.dns_records.every(isIntegrationDnsRecord) &&
    typeof row.verification_status === "string" &&
    typeof row.dkim_status === "string" &&
    typeof row.mail_from_status === "string" &&
    (row.last_checked_at === null || typeof row.last_checked_at === "string")
  );
}

function isIntegrationDetail(value: unknown): value is IntegrationDetail {
  if (!isIntegrationSummary(value)) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return row.email === null || row.email === undefined || isEmailIntegrationDetails(row.email);
}

function parseIntegrationsResponse(data: unknown): IntegrationSummary[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  return data.filter(isIntegrationSummary);
}

export const fetchIntegrations = createAsyncThunk<
  IntegrationSummary[],
  void,
  { rejectValue: string }
>("integrations/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/integrations", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    const connections = parseIntegrationsResponse(data);
    if (!connections) {
      return rejectWithValue("Unexpected response shape");
    }
    return connections;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export type CreateIntegrationPayload = {
  name?: string;
  description?: string;
  type: string;
  config: Record<string, unknown>;
};

export const createIntegration = createAsyncThunk<
  IntegrationSummary,
  CreateIntegrationPayload,
  { rejectValue: string }
>("integrations/create", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/integrations", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isIntegrationSummary(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const fetchIntegrationDetail = createAsyncThunk<
  IntegrationDetail,
  number,
  { rejectValue: string }
>("integrations/fetchDetail", async (integrationId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/integrations/${integrationId}`, { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isIntegrationDetail(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

const integrationsSlice = createSlice({
  name: "integrations",
  initialState: integrationsInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchIntegrations.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchIntegrations.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.connections = action.payload;
      })
      .addCase(fetchIntegrations.rejected, (state) => {
        state.status = STATUS.FAILED;
        state.connections = [];
      })
      .addCase(createIntegration.fulfilled, (state, action) => {
        state.connections = [...state.connections, action.payload];
      })
      .addCase(fetchIntegrationDetail.pending, (state, action) => {
        state.detail.status = STATUS.LOADING;
        state.detail.error = null;
        state.detail.requestedId = action.meta.arg;
        // Drop another integration's data so its details never paint under this one's header.
        if (state.detail.data !== null && state.detail.data.id !== action.meta.arg) {
          state.detail.data = null;
        }
      })
      .addCase(fetchIntegrationDetail.fulfilled, (state, action) => {
        if (state.detail.requestedId !== action.meta.arg) {
          return;
        }
        state.detail.status = STATUS.SUCCEEDED;
        state.detail.data = action.payload;
      })
      .addCase(fetchIntegrationDetail.rejected, (state, action) => {
        if (state.detail.requestedId !== action.meta.arg) {
          return;
        }
        state.detail.status = STATUS.FAILED;
        state.detail.error = action.payload ?? null;
      });
  },
});

export const integrationsReducer = integrationsSlice.reducer;
